<template>
  <div class="card card-body officer-details-page">
    <div v-if="officer">

      <!-- =======================
          PROFILE HEADER
      ======================== -->
      <section class="mb-4 text-center">
        <img
          :src="passportPhotoUrl ? apiBaseUrl + passportPhotoUrl : defaultPhoto"
          class="rounded-circle border border-secondary shadow-sm mb-2"
          style="width: 6rem; height: 6rem; object-fit: cover;"
        />

        <h4 class="text-primary font-weight-bold mt-2">
          {{ officer.fullName || 'N/A' }}
        </h4>

        <p class="text-muted mb-0">{{ officer.email || 'No email' }}</p>
        <p class="text-muted">{{ officer.phoneNumber || 'No phone' }}</p>
      </section>

      <!-- =======================
          PERSONAL INFORMATION
      ======================== -->
      <section class="mb-4">
        <h5 class="text-primary border-bottom pb-2 mb-3">Personal Information</h5>

        <b-row class="mb-3">
          <b-col md="6" class="mb-2">
            <strong>ID Number:</strong>
            <div class="text-muted">{{ officer.idNumber || 'N/A' }}</div>
          </b-col>

          <b-col md="6" class="mb-2">
            <strong>Date of Birth:</strong>
            <div class="text-muted">{{ formattedDob }}</div>
          </b-col>

          <b-col md="6" class="mb-2">
            <strong>Gender:</strong>
            <div class="text-muted">{{ officer.gender || 'N/A' }}</div>
          </b-col>

          <b-col md="6" class="mb-2">
            <strong>Bank Details:</strong>
            <div class="text-muted">{{ officer.bankDetails || 'N/A' }}</div>
          </b-col>
        </b-row>
      </section>

      <!-- =======================
          ASSIGNMENT DETAILS
      ======================== -->
      <section class="mb-4" v-if="officer.stationId">
        <h5 class="text-primary border-bottom pb-2 mb-3">Assignment Details</h5>

        <b-row>
          <b-col md="6" class="mb-2">
            <strong>Assigned Station:</strong>
            <div class="text-muted">
              {{ officer.stationName || 'N/A' }} (ID: {{ officer.stationId }})
            </div>
          </b-col>
        </b-row>
      </section>

      <!-- =======================
          NEXT OF KIN
      ======================== -->
      <section class="mb-4" v-if="officer.nextOfKins?.length">
        <h5 class="text-primary border-bottom pb-2 mb-3">Next of Kin</h5>

        <div
          v-for="(nok, index) in officer.nextOfKins"
          :key="index"
          class="mb-2 p-2 border rounded"
        >
          <b-row>
            <b-col md="4">
              <strong>Name:</strong>
              <div class="text-muted">{{ nok.name || 'N/A' }}</div>
            </b-col>

            <b-col md="4">
              <strong>Phone:</strong>
              <div class="text-muted">{{ nok.phone || 'N/A' }}</div>
            </b-col>

            <b-col md="4">
              <strong>Relationship:</strong>
              <div class="text-muted">{{ nok.relationship || 'N/A' }}</div>
            </b-col>
          </b-row>
        </div>
      </section>

      <!-- =======================
          LOCATIONS
      ======================== -->
      <section class="mb-4" v-if="officer.locations?.length">
        <h5 class="text-primary border-bottom pb-2 mb-3">Locations</h5>

        <div
          v-for="(loc, index) in officer.locations"
          :key="index"
          class="mb-2 p-2 border rounded"
        >
          <b-row>
            <b-col md="4">
              <strong>County:</strong>
              <div class="text-muted">{{ loc.county || 'N/A' }}</div>
            </b-col>

            <b-col md="4">
              <strong>Sub-County:</strong>
              <div class="text-muted">{{ loc.subCounty || 'N/A' }}</div>
            </b-col>

            <b-col md="4">
              <strong>Ward:</strong>
              <div class="text-muted">{{ loc.ward || 'N/A' }}</div>
            </b-col>

            <b-col md="12">
              <strong>GPS:</strong>
              <div class="text-muted">{{ loc.gps || 'N/A' }}</div>
            </b-col>
          </b-row>
        </div>
      </section>

      <!-- =======================
          DOCUMENTS
      ======================== -->
      <section class="mt-4">
        <h5 class="text-primary border-bottom pb-2 mb-3">Documents</h5>

        <b-row>
          <b-col md="6">
            <strong>ID Document:</strong>
            <div>
              <a v-if="idDocumentUrl" :href="apiBaseUrl + idDocumentUrl" target="_blank">View Document</a>
              <span v-else class="text-muted">Not uploaded</span>
            </div>
          </b-col>
        </b-row>

        <div v-if="otherDocuments.length" class="mt-3">
          <h6>Other Documents:</h6>
          <b-table
            :items="otherDocuments"
            :fields="[
              { key: 'fileName', label: 'File Name' },
              { key: 'contentType', label: 'Type' },
              { key: 'fileUrl', label: 'Action' }
            ]"
            small
            striped
            responsive
          >
            <template #cell(fileUrl)="data">
              <a :href="apiBaseUrl + data.item.fileUrl" target="_blank">View</a>
            </template>
          </b-table>
        </div>

        <div v-else-if="!idDocumentUrl" class="text-muted mt-2">
          No documents uploaded.
        </div>
      </section>

      <!-- =======================
          SYSTEM ACCOUNT
      ======================== -->
      <section class="mt-4" v-if="officer.systemUserId">
        <h5 class="text-primary border-bottom pb-2 mb-3">System User Account</h5>

        <b-row>
          <b-col md="6">
            <strong>Username:</strong>
            <div class="text-muted">{{ officer.systemUserName }}</div>
          </b-col>

          <b-col md="6">
            <strong>Email:</strong>
            <div class="text-muted">{{ officer.systemUserEmail }}</div>
          </b-col>

          <b-col md="6">
            <strong>System User ID:</strong>
            <div class="text-muted">{{ officer.systemUserId }}</div>
          </b-col>
        </b-row>
      </section>

      <!-- EDIT BUTTON -->
      <div class="text-end mt-4">
        <button class="btn btn-primary" @click="editOfficer">
          <i class="bi bi-pencil-square me-2"></i>Edit Officer
        </button>
      </div>

    </div>

    <div v-else class="text-center py-5 text-muted">
      <b-spinner small class="mr-2" /> Loading officer details...
    </div>

  </div>
</template>

<script>
export default {
  name: "OfficerDetailsPage",

  data() {
    return {
      officer: null,
      apiBaseUrl: "",
      defaultPhoto: "https://via.placeholder.com/100?text=Photo"
    };
  },

  created() {
    const saved = localStorage.getItem("officerToView");
    this.officer = saved ? JSON.parse(saved) : null;
  },

  computed: {
    formattedDob() {
      if (!this.officer?.dob) return "N/A";
      const date = new Date(this.officer.dob);
      return isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString();
    },

    passportPhotoUrl() {
      const doc = (this.officer?.documents || []).find(d =>
        d.contentType?.toUpperCase().includes("PASSPORT")
      );
      return doc?.fileUrl || this.officer?.passportPhotoPath || null;
    },

    idDocumentUrl() {
      const doc = (this.officer?.documents || []).find(d =>
        d.contentType?.toUpperCase().includes("ID")
      );
      return doc?.fileUrl || null;
    },

    otherDocuments() {
      const excluded = new Set([this.passportPhotoUrl, this.idDocumentUrl]);
      return (this.officer?.documents || []).filter(d => d.fileUrl && !excluded.has(d.fileUrl));
    }
  },

  methods: {
    goBack() {
      this.$router.back();
    },

    editOfficer() {
      if (this.officer?.id) {
        this.$router.push({
          name: "Entry",
          params: { officerId: this.officer.id },
          query: { menu: "New Officer" }
        });
      }
    }
  }
};
</script>

<style scoped>
.text-primary.border-bottom {
  border-color: #007bff !important;
}
</style>
